import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquarePlus, Sparkles, Rocket, Quote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { waLink } from '../../config';

/* ──────────────────────────────────────────────────────────────────────────
   SOCIAL PROOF — Métricas grandes + caso real en producción (Prodental 3D).
   Credibilidad: "ya funciona, hay un socio real detrás".
   Antes había 3 testimonios inventados (nombre/clínica ficticios) presentados
   como reseñas reales de 5 estrellas — se eliminaron por completo. Con un
   único cliente real y todavía en fase piloto, más vale un caso honesto y
   bien mostrado que "ejemplos ilustrativos" que se confunden con reseñas.
   ────────────────────────────────────────────────────────────────────────── */

const SocialProof: React.FC = () => {
  const { t } = useTranslation();

  const stats = [
    { value: '24/7', label: t('socialproof.stat1', 'tu agente responde a cualquier hora, incluso de madrugada'), accent: '#00e5d1' },
    { value: '< 1 min', label: t('socialproof.stat2', 'es lo que tarda en contestar un mensaje nuevo'), accent: '#8b5cf6' },
    { value: '0', label: t('socialproof.stat3', 'pacientes perdidos por un mensaje sin responder'), accent: '#00e5d1' },
  ];

  const caseStats = [
    { value: t('socialproof.case_stat1_value', '712'), label: t('socialproof.case_stat1_label', 'conversaciones atendidas por IA este mes') },
    { value: t('socialproof.case_stat2_value', '96,3%'), label: t('socialproof.case_stat2_label', 'de automatización') },
    { value: t('socialproof.case_stat3_value', '~95h'), label: t('socialproof.case_stat3_label', 'de trabajo manual ahorradas') },
  ];

  return (
    <section id="resultados" style={{ padding: 'clamp(4.5rem, 9vh, 7.5rem) 0', position: 'relative' }}>
      <div className="container">

        {/* Banda de métricas del producto (genéricas, no atadas a un cliente) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem', marginBottom: '5rem' }}>
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ delay: i * 0.12, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              style={{ textAlign: 'center' }}
            >
              <div style={{
                fontSize: 'clamp(3rem, 6vw, 4.5rem)', fontWeight: 800, lineHeight: 1,
                letterSpacing: '-0.03em', marginBottom: '0.8rem',
                background: 'linear-gradient(135deg, #00f0ff 0%, #9d6bff 100%)',
                WebkitBackgroundClip: 'text', backgroundClip: 'text',
                WebkitTextFillColor: 'transparent', color: 'transparent',
              }}>
                {s.value}
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.98rem', lineHeight: 1.5, maxWidth: 240, margin: '0 auto' }}>
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Caso real — Prodental 3D. No es una clínica dental (es una empresa de
            tecnología dental: digitaliza clínicas con flujo CAD/CAM), así que se
            etiqueta como tal en vez de sugerir que es del nicho principal.
            Único cliente real hoy, todavía en fase piloto: se le da protagonismo
            propio en vez de mezclarlo con testimonios inventados. */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="sp-case-card"
          style={{ position: 'relative', overflow: 'hidden', maxWidth: 1200, width: '100%', margin: '0 auto', borderRadius: 28, padding: 'clamp(2.2rem, 4vw, 3.2rem)' }}
        >
          <div aria-hidden style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: '#00e5d1', opacity: 0.12, filter: 'blur(70px)', pointerEvents: 'none' }} />
          <div aria-hidden style={{ position: 'absolute', bottom: -80, left: -60, width: 220, height: 220, borderRadius: '50%', background: '#8b5cf6', opacity: 0.1, filter: 'blur(70px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.4rem' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '.4rem',
                fontSize: '.72rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                color: 'var(--accent-teal)', background: 'rgba(0,229,209,.1)', border: '1px solid rgba(0,229,209,.3)',
                padding: '.35rem .85rem', borderRadius: 999,
              }}>
                <Sparkles size={13} strokeWidth={2.5} />
                {t('socialproof.case_badge', 'Tecnología dental · Caso real')}
              </span>
            </div>

            <div className="sp-case-head">
              <h3 style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2rem)', fontWeight: 300, lineHeight: 1.3, letterSpacing: '-0.02em', color: 'var(--text-primary)', margin: 0, textWrap: 'balance' } as React.CSSProperties}>
                <span className="grad-text" style={{ fontWeight: 800 }}>Prodental 3D</span>{' '}{t('socialproof.case_heading_rest', 'ya automatiza su WhatsApp con RurayHUB')}
              </h3>

              <p style={{ fontSize: '1rem', lineHeight: 1.6, color: 'var(--text-secondary)', margin: 0 }}>
                {t('socialproof.case_desc', 'Prodental 3D lleva clínicas dentales al flujo digital CAD/CAM — software, escáneres e impresoras 3D. Automatizamos el WhatsApp de su equipo comercial con un agente de IA propio.')}
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.2rem', paddingTop: '1.6rem', borderTop: '1px solid var(--border-light)' }}>
              {caseStats.map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.4rem, 3vw, 1.9rem)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)', marginBottom: '.3rem', fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
                  <div style={{ fontSize: '.78rem', lineHeight: 1.4, color: 'var(--text-secondary)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Cita real del dueño/gerente general de Prodental 3D — autorizada
                explícitamente por él para publicarse con su nombre y cargo.
                Sin estrellas (a propósito: eso es lo que hacía parecer reales
                a los testimonios inventados de antes). El avatar son las
                iniciales con el gradiente de marca — no una foto, porque no
                tenemos una real y una genérica de stock sería tan deshonesta
                como el problema que se corrigió; si más adelante hay logo o
                foto real de Prodental 3D, se reemplaza acá. La frase
                destacada usa teal sólido (no el gradient-clip que sí usa el
                nombre de la empresa arriba) porque a tamaño de texto de
                cuerpo, con peso normal, el tramo morado del degradado cae
                por debajo de contraste legible en fondo oscuro. */}
            <div className="sp-case-quote">
              <Quote size={26} color="var(--accent-teal)" style={{ opacity: 0.5 }} />
              <p className="sp-case-quote-text">
                "{t('socialproof.case_quote_pre', 'Antes me dormía preocupado pensando en los mensajes que no iba a poder responder — al ser una empresa internacional, nos escriben a cualquier hora. ')}
                <span style={{ color: 'var(--accent-teal)', fontWeight: 700 }}>{t('socialproof.case_quote_highlight', 'Ahora el agente responde por nosotros mientras dormimos')}</span>
                {t('socialproof.case_quote_post', ', y la inversión ya se recuperó sola.')}"
              </p>
              <div className="sp-case-quote-author">
                <span className="sp-avatar">GM</span>
                <div>
                  <div className="sp-avatar-name">{t('socialproof.case_quote_name', 'Gustavo Mendez')}</div>
                  <div className="sp-avatar-role">{t('socialproof.case_quote_role', 'Gerente General de Prodental 3D')}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Invitación honesta: en vez de fingir más clientes de los que hay,
            se convierte la fase piloto en una llamada a la acción. */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ maxWidth: 1200, margin: '2.2rem auto 0', textAlign: 'center' }}
        >
          <p style={{ fontSize: '.92rem', color: 'var(--text-muted)', margin: '0 0 1.1rem' }}>
            {t('socialproof.pilot_text', 'Estamos seleccionando clínicas dentales para las próximas implementaciones piloto.')}
          </p>
          <div style={{ display: 'flex', gap: '1.4rem', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
            <a
              href={waLink(t('socialproof.pilot_cta_msg', 'Hola, tengo una clínica dental y quiero ser parte de las implementaciones piloto de RurayHUB 🦷'))}
              target="_blank" rel="noopener noreferrer"
              className="trial-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '.6rem', textDecoration: 'none', borderRadius: 980, padding: '.9rem 1.6rem', fontWeight: 700, fontSize: '.95rem' }}
            >
              <Rocket size={17} strokeWidth={2.5} />
              {t('socialproof.pilot_cta', 'Quiero ser clínica piloto')}
            </a>
            <a
              href={waLink(t('socialproof.review_cta_msg', 'Hola, soy cliente de RurayHUB y quiero dejar mi reseña 🙌'))}
              target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem', textDecoration: 'none', color: 'var(--text-muted)', fontSize: '.85rem', fontWeight: 600 }}
            >
              <MessageSquarePlus size={15} strokeWidth={2.5} />
              {t('socialproof.review_cta', '¿Ya eres cliente? Déjanos tu reseña')}
            </a>
          </div>
        </motion.div>
      </div>

      <style>{`
        .sp-case-card {
          background: var(--bg-elevated);
          border: 1px solid var(--border-light);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }
        [data-theme='light'] .sp-case-card {
          background: #fff;
          box-shadow: 0 25px 60px -30px rgba(0,0,0,0.15);
        }

        .sp-case-quote {
          margin-top: 1.6rem;
          padding-top: 1.6rem;
          border-top: 1px solid var(--border-light);
        }

        .sp-case-quote-text {
          font-size: .98rem;
          font-weight: 300;
          line-height: 1.55;
          text-align: justify;
          text-justify: inter-word;
          hyphens: auto;
          color: var(--text-primary);
          letter-spacing: -0.005em;
          margin: .6rem 0 1.2rem;
          max-width: none;
        }

        .sp-case-head {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2.4rem;
          align-items: center;
          margin-bottom: 2rem;
        }
        @media (max-width: 700px) {
          .sp-case-head {
            grid-template-columns: 1fr;
            gap: 0.9rem;
          }
        }

        .sp-case-quote-author {
          display: flex;
          align-items: center;
          gap: .7rem;
        }

        .sp-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-heading);
          font-weight: 800;
          font-size: .78rem;
          letter-spacing: -0.02em;
          color: #fff;
          background: linear-gradient(135deg, #00e5d1 0%, #8b5cf6 100%);
          box-shadow: 0 0 0 3px rgba(0,229,209,.1);
        }

        .sp-avatar-name {
          font-weight: 700;
          font-size: .88rem;
          color: var(--text-primary);
        }

        .sp-avatar-role {
          font-size: .78rem;
          color: var(--text-muted);
        }
      `}</style>
    </section>
  );
};

export default SocialProof;
